/**
 * Sapp's Coaching Pillars / Why Us Section
 * Design: White background, blue icon cards, navy quote callout
 * Content: The 6 core pillars
 */
import { useEffect, useRef } from "react";
import { Dumbbell, Apple, Target, Users, Heart, Sparkles } from "lucide-react";

const pillars = [
  {
    icon: Dumbbell,
    title: "Personal Training",
    description:
      "Custom strength and cardio programming built for your body, goals, and available equipment. Every workout is designed by a certified coach — not a generic template.",
  },
  {
    icon: Apple,
    title: "Nutrition Coaching",
    description:
      "Personalized meal plans with precise macro calculations. We don't do one-size-fits-all diets. We build a nutrition strategy around your life, preferences, and health needs.",
  },
  {
    icon: Target,
    title: "Accountability",
    description:
      "A dedicated coach checks in with you every week — via Zoom, text, or phone. When life gets hard, your coach keeps you on track. This is the difference-maker.",
  },
  {
    icon: Users,
    title: "Community",
    description:
      "You're not doing this alone. Sapp's Coaching clients support each other, celebrate wins, and push through challenges together. The community is part of the transformation.",
  },
  {
    icon: Heart,
    title: "Support",
    description:
      "Unlimited messaging with your coach. Questions, adjustments, encouragement — we're here whenever you need us. You'll never feel like just a number.",
  },
  {
    icon: Sparkles,
    title: "Transformation",
    description:
      "We're not just changing bodies. We're changing lives. Confidence, energy, health, mindset — Sapp's Coaching delivers a complete transformation that lasts.",
  },
];

export default function PillarsSection() {

  return (
    <section
      className="pt-16 lg:pt-20 pb-0"
      style={{ background: "oklch(0.97 0.004 40)" }}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="animate-on-scroll eyebrow mb-3">The Sapp's Coaching Difference</div>
          <h2
            className="animate-on-scroll animate-on-scroll-delay-1 section-headline mb-4"
            style={{ fontSize: "clamp(2.8rem, 5vw, 4.2rem)", color: "oklch(0.18 0.02 40)" }}
          >
            Six Pillars of
            <br />
            <span style={{ color: "oklch(0.42 0.20 22)" }}>Complete Transformation</span>
          </h2>
          <p
            className="animate-on-scroll animate-on-scroll-delay-2 max-w-2xl mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "1.12rem", color: "oklch(0.45 0.02 260)" }}
          >
            Most programs give you one piece of the puzzle. Sapp's Coaching gives you
            everything — the complete system that addresses every aspect of your
            transformation journey.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <div
              key={pillar.title}
              className={`animate-on-scroll animate-on-scroll-delay-${(index % 5) + 1} card-white p-6 rounded-2xl`}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: "oklch(0.95 0.03 22)" }}
              >
                <pillar.icon size={22} style={{ color: "oklch(0.42 0.20 22)" }} />
              </div>
              <h3
                className="font-bold mb-3"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.5rem", color: "oklch(0.18 0.02 40)" }}
              >
                {pillar.title}
              </h3>
              <p
                className="leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "1rem", color: "oklch(0.45 0.02 260)" }}
              >
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Quote callout — navy */}
        <div
          className="animate-on-scroll mt-14 p-8 lg:p-12 rounded-2xl text-center"
          style={{
            background: "oklch(0.14 0.02 40)",
          }}
        >
          <blockquote
            className="section-headline text-white mb-4"
            style={{ fontSize: "clamp(2rem, 3.8vw, 3.2rem)" }}
          >
            "This is more than a gym.
            <br />
            <span style={{ color: "oklch(0.80 0.17 80)" }}>
              This is a complete transformation system."
            </span>
          </blockquote>
          <p
            className="text-white/65 text-sm"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            — The Sapp's Coaching Promise
          </p>
        </div>
      </div>
    </section>
  );
}
